import React, { ChangeEvent, useEffect } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    FormControlLabel,
    Radio,
    RadioGroup as MUIRadioGroup,
    RadioGroupProps,
    Typography,
} from '@mui/material';
import { InfoText, TextField } from '@fnol/library/src/components';
import { useApi } from '@fnol/library/src/hooks';
import { GenericObject } from '@fnol/library/src/lib';
import {
    bankType,
    translations,
    questionMaxLength,
    question50MaxLength,
    personType,
    riskTypeLife,
} from '../../lib/consts';
import { getBankAccounts } from '../../rpc/actions';

type PersonProps = {
    type: personType;
    id?: number;
};

const PaymentType = ({ type, id }: PersonProps) => {
    const { values, errors, touched, setFieldValue } = useFormikContext<GenericObject>();
    const { response: bankAccounts, fetchMethod: fetchBankAccounts } = useApi(getBankAccounts);

    const idUser = id ? id : '';
    const bank = `${personType[type]}${idUser}Bank`;
    const bankName = `${personType[type]}${idUser}BankName`;
    const accountNumber = `${personType[type]}${idUser}AccountNumber`;

    useEffect(() => {
        fetchBankAccounts();
        values.currency === 'DIN' ? setFieldValue(bank, bankType.domestic) : setFieldValue(bank, values[bank]);
    }, []);

    const handleChangeBank = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue(bank, event.target.value);
        setFieldValue(accountNumber, '');
        setFieldValue(bankName, '');
    };

    const RadioGroup = (params: RadioGroupProps) => {
        return (
            <>
                <InfoText label={translations.policyUser.PAYMENT_REMARK}></InfoText>
                <MUIRadioGroup {...params} sx={{ flexDirection: 'row' }}>
                    {!values.currency && (
                        <FormControlLabel
                            value={bankType.domestic}
                            control={<Radio />}
                            label={
                                values.risks.includes(riskTypeLife.purchase) && values.userTypePurchase === 2
                                    ? translations.policyUser.DOMESTIC_LEGAL_ENTITY
                                    : translations.policyUser.DOMESTIC
                            }
                        />
                    )}
                    {values.currency !== 'DIN' && (
                        <>
                            <FormControlLabel
                                value={bankType.iban}
                                control={<Radio />}
                                label={
                                    values.risks.includes(riskTypeLife.purchase) && values.userTypePurchase === 2
                                        ? translations.policyUser.DOMESTIC_IBAN_LEGAL_ENTITY
                                        : translations.policyUser.DOMESTIC_IBAN
                                }
                            />
                            <FormControlLabel
                                value={bankType.internacional}
                                control={<Radio />}
                                label={translations.policyUser.INTERNACIONAL}
                            />
                        </>
                    )}
                </MUIRadioGroup>
            </>
        );
    };

    const getBankName = () => {
        let sliceStart = 0; // Bank code starts at 0 for domestic bank account
        if (values[bank] === bankType.iban) sliceStart = 4; // Bank code starts at 4 for Serbian IBAN RS35<BankCode>
        const bankEntry = bankAccounts.data.data?.find(
            (item: GenericObject) => item.tekuciRacun === values[accountNumber]?.slice(sliceStart, sliceStart + 3),
        );
        return bankEntry ? bankEntry.naziv : '';
    };

    useEffect(() => {
        if (!values[accountNumber]) {
            setFieldValue(bankName, '');
        }
        if (
            values[accountNumber] &&
            !!getBankName() &&
            (values[bank] === bankType.domestic || values[bank] === bankType.iban)
        ) {
            setFieldValue(bankName, getBankName());
        }
    }, [values[accountNumber]]);

    const handleChangeAccount = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue(accountNumber, e.target.value);

        if ((values[accountNumber] || !values[accountNumber]) && !getBankName()) {
            setFieldValue(bankName, '');
        }
    };

    return (
        <>
            <FormControl>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {translations.policyUser.PAYMENT_DATA}
                </Typography>
                <Field type="boolean" name={bank} as={RadioGroup} onChange={handleChangeBank} />
                <FormHelperText error={true}>
                    <>{errors[bank]}</>
                </FormHelperText>
            </FormControl>
            <FormControl>
                <Field
                    name={accountNumber}
                    label={translations.policyUser.ACCOUNT_NUM}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[accountNumber] && errors[accountNumber])}
                    value={values[accountNumber]}
                    onChange={handleChangeAccount}
                    required
                />
                {touched[accountNumber] && errors[accountNumber] ? (
                    <FormHelperText error={true}>
                        <>{errors[accountNumber]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <FormControl>
                <Field
                    name={bankName}
                    label={translations.policyUser.BANK_NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    error={!!(touched[bankName] && errors[bankName])}
                    required
                />
                {touched[bankName] && errors[bankName] ? (
                    <FormHelperText error={true}>
                        <>{errors[bankName]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
        </>
    );
};

export const PaymentCleanup = (
    type: personType,
    setFieldValue: (field: string, value: string | number, shouldValidate?: boolean | undefined) => void,
    userId?: number,
) => {
    const idUser = userId ? userId : '';
    setFieldValue(`${personType[type]}${idUser}Bank`, '');
    setFieldValue(`${personType[type]}${idUser}BankName`, '');
    setFieldValue(`${personType[type]}${idUser}AccountNumber`, '');
};

export default PaymentType;
